import { RoutingEndpoints } from '@/constants';
import { UserOrganization } from '@/modules/Users/domain';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import OrganizationPickerPage from '../OrganizationPickerPage/OrganizationPickerPage';

type OrganizationContextState = {
  organizationId?: number;
  setOrganizationId: Dispatch<SetStateAction<number | undefined>>;
};

export const OrganizationContext = createContext<OrganizationContextState>({
  organizationId: undefined,
  setOrganizationId: function (
    _value: SetStateAction<number | undefined>,
  ): void {
    throw new Error('Function not implemented.');
  },
});

type OrganizationProviderProps = PropsWithChildren<{
  userOrganizations: UserOrganization[];
}>;

export default function OrganizationProvider({
  userOrganizations,
  children,
}: OrganizationProviderProps) {
  const navigate = useNavigate();
  const { organizationId: organizationIdParam } = useParams<{
    organizationId: string;
  }>();
  const [organizationId, setOrganizationId] = useState(() => {
    if (organizationIdParam) {
      return +organizationIdParam;
    }

    const organizationIdSession = sessionStorage.getItem('organizationId');

    if (organizationIdSession) {
      return +organizationIdSession;
    }

    if (userOrganizations.length === 1) {
      return userOrganizations[0].id;
    }
  });

  useEffect(() => {
    if (organizationId) {
      sessionStorage.setItem('organizationId', organizationId.toString());
      navigate(`/organization/${organizationId}`);
    } else {
      sessionStorage.removeItem('organizationId');
    }
  }, [navigate, organizationId]);

  if (!organizationId) {
    return (
      <OrganizationPickerPage
        organizations={userOrganizations}
        onSelect={setOrganizationId}
      />
    );
  }

  const hasAccess = userOrganizations.some((org) => org.id === organizationId);

  if (!hasAccess) {
    sessionStorage.removeItem('organizationId');
    navigate(RoutingEndpoints.notFound(), { replace: true });
    return;
  }

  return (
    <OrganizationContext.Provider value={{ organizationId, setOrganizationId }}>
      {children}
    </OrganizationContext.Provider>
  );
}
